<template>
  <div class="yearly-targets">
    <div class="container">
      <div class="header">
        <h1>Yearly Targets</h1>
        <p>Set and manage monthly financial targets for branches within subsidiaries</p>
      </div>

      <div class="records-container">
        <div class="card-header">
          <div class="header-icon">
            <i class="fa-solid fa-bullseye"></i>
          </div>
          <h2>Target Management</h2>
        </div>

        <!-- Tabs -->
        <div class="tabs">
          <button
            :class="['tab-btn', { active: activeTab === 'targets' }]"
            @click="activeTab = 'targets'; fetchTargets()"
          >
            <i class="fa-solid fa-list"></i> Targets
          </button>
          <button
            :class="['tab-btn', { active: activeTab === 'bulk' }]"
            @click="activeTab = 'bulk'; fetchBulkData()"
          >
            <i class="fa-solid fa-layer-group"></i> Bulk Add
          </button>
        </div>

        <!-- Tab: Targets -->
        <template v-if="activeTab === 'targets'">
        <!-- Filters -->
        <div class="filters">
          <div class="form-group">
            <label>Filter by Year</label>
            <select v-model="selectedYear" @change="fetchTargets">
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
        </div>

        <!-- Add Target Button -->
        <div class="action-bar">
          <button class="export-btn" @click="exportToCSV">
            <i class="fa-solid fa-file-export"></i>
            Export to CSV
          </button>
          <button class="add-btn" @click="showAddDialog">
            <i class="fa-solid fa-plus"></i> Add Target
          </button>
        </div>

        <!-- Targets Table -->
        <div class="targets-table">
          <table>
            <thead>
              <tr>
                <th></th> <!-- For expand/collapse icon -->
                <th>Subsidiary</th>
                <th>Yearly Target</th>
                <th v-if="!isMobile">Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="subsidiaryTargets.length === 0">
                <td :colspan="isMobile ? 4 : 5" class="no-data">
                  <div class="no-data-message">
                    <i class="fa-solid fa-circle-info"></i>
                    <p>No targets found for {{ selectedYear }}</p>
                  </div>
                </td>
              </tr>
              <template v-for="subsidiary in subsidiaryTargets" :key="subsidiary.id">
                <!-- Subsidiary Row -->
                <tr class="subsidiary-row">
                  <td>
                    <button 
                      class="expand-button"
                      @click="toggleExpand(subsidiary.id)"
                    >
                      <i :class="expandedRows.includes(subsidiary.id) ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"></i>
                    </button>
                  </td>
                  <td>{{ subsidiary.name }}</td>
                  <td><CurrencyDisplay :value="subsidiary.yearly_target" /></td>
                  <td v-if="!isMobile">{{ subsidiary.created_by_name }}</td>
                  <td>
                    <div class="action-buttons">
                      <button class="edit-btn" @click="editTarget(subsidiary)">
                        <i class="fa-solid fa-edit"></i>
                      </button>
                      <button class="delete-btn" @click="deleteTarget(subsidiary)">
                        <i class="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <!-- Branch Rows (Expanded) -->
                <tr v-if="expandedRows.includes(subsidiary.id)" class="branch-rows">
                  <td colspan="5">
                    <table class="branch-table">
                      <thead>
                        <tr>
                          <th>Branch</th>
                          <th>Monthly Target</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="target in getBranchTargets(subsidiary.id)" :key="target.branch_id">
                          <td>{{ target.branch_name }}</td>
                          <td><CurrencyDisplay :value="target.monthly_target" /></td>
                          <td>
                            <div class="action-buttons">
                              <button class="edit-btn" @click="editBranchTarget(target)">
                                <i class="fa-solid fa-edit"></i>
                              </button>
                              <button class="delete-btn" @click="deleteBranchTarget(target)">
                                <i class="fa-solid fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        </template>

        <!-- Tab: Bulk Add -->
        <template v-if="activeTab === 'bulk'">
          <div class="filters">
            <div class="form-group">
              <label>Year</label>
              <select v-model="selectedYear" @change="fetchBulkData">
                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>
          <div class="action-bar">
            <button class="add-btn" @click="saveBulkTargets" :disabled="savingBulk">
              <i class="fa-solid" :class="savingBulk ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              Save All
            </button>
          </div>
          <div class="bulk-table">
            <table>
              <thead>
                <tr>
                  <th>Subsidiary</th>
                  <th>Yearly Target (RM)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in bulkRows" :key="row.subsidiary_id">
                  <td>{{ row.subsidiary_name }}</td>
                  <td>
                    <input
                      v-model.number="row.yearly_target"
                      type="number"
                      class="target-input"
                      placeholder="0"
                      min="0"
                      step="0.01"
                    >
                  </td>
                </tr>
                <tr v-if="!bulkRows.length">
                  <td colspan="2" class="no-data">
                    <p>No subsidiaries found. Add subsidiaries first.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>

    <!-- Add/Edit Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="getDialogTitle"
      width="500px"
      class="target-dialog"
    >
      <template v-if="isEditing && isEditingSubsidiary">
        <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
          <el-form-item label="Subsidiary">
            <span>{{ editingSubsidiary?.name }}</span>
          </el-form-item>
          <el-form-item label="Year">
            <span>{{ form.year }}</span>
          </el-form-item>
        </el-form>
        <div class="branches-table">
          <h3>Branches for {{ editingSubsidiary?.name }}</h3>
          <table>
            <thead>
              <tr>
                <th>Branch Name</th>
                <th>Monthly Target</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="branch in getSubsidiaryBranches" :key="branch.id">
                <td>{{ branch.branch_name }}</td>
                <td>
                  <el-input-number
                    v-model="branch.target_amount"
                    :min="0"
                    :precision="2"
                    :step="1000"
                    class="full-width"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template v-else-if="isEditing && !isEditingSubsidiary">
        <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
          <el-form-item label="Branch">
            <span>{{ editingBranch?.branch_name }}</span>
          </el-form-item>
          <el-form-item label="Monthly Target" prop="target_amount">
            <el-input-number
              v-model="form.target_amount"
              :min="0"
              :precision="2"
              :step="1000"
              class="full-width"
            />
          </el-form-item>
        </el-form>
      </template>
      <template v-else>
        <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
          <el-form-item label="Subsidiary" prop="subsidiary_id">
            <el-select v-model="form.subsidiary_id" placeholder="Select Subsidiary" class="full-width" @change="fetchBranches(form.subsidiary_id)">
              <el-option
                v-for="sub in subsidiaries"
                :key="sub.id"
                :label="sub.name"
                :value="sub.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Year" prop="year">
            <el-select v-model="form.year" placeholder="Select Year" class="full-width">
              <el-option
                v-for="year in years"
                :key="year"
                :label="year"
                :value="year"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <div v-if="filteredBranches.length > 0" class="branches-table">
          <h3>Branches for {{ subsidiaries.find(s => s.id === form.subsidiary_id)?.name }}</h3>
          <table>
            <thead>
              <tr>
                <th>Branch Name</th>
                <th>Monthly Target</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="branch in filteredBranches" :key="branch.id">
                <td>{{ branch.branch_name }}</td>
                <td>
                  <el-input-number
                    v-model="branch.target_amount"
                    :min="0"
                    :precision="2"
                    :step="1000"
                    class="full-width"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">Cancel</el-button>
          <el-button type="primary" @click="submitForm">Save</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// State
const targets = ref([]);
const subsidiaries = ref([]);
const branches = ref([]);
const loading = ref(false);
const dialogVisible = ref(false);
const isEditing = ref(false);
const isEditingSubsidiary = ref(false);
const selectedYear = ref(new Date().getFullYear());
const formRef = ref(null);
const subsidiaryTotals = ref([]);
const editingBranch = ref(null);
const editingSubsidiary = ref(null);
const activeTab = ref('targets');
const bulkRows = ref([]);
const savingBulk = ref(false);

const form = ref({
  subsidiary_id: '',
  year: new Date().getFullYear()
});

// Computed
const years = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => currentYear + i);
});

const months = computed(() => {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString('default', { month: 'long' })
  }));
});

const isMobile = computed(() => window.innerWidth <= 768);

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

// Rules
const rules = {
  subsidiary_id: [{ required: true, message: 'Please select a subsidiary', trigger: 'change' }],
  year: [{ required: true, message: 'Please select a year', trigger: 'change' }],
  target_amount: [{ required: true, message: 'Please enter monthly target', trigger: 'blur' }]
};

// Methods
const fetchTargets = async () => {
  try {
    loading.value = true;
    const response = await api.get('/api/finance/branch-monthly-targets', {
      params: { year: selectedYear.value }
    });
    targets.value = response.data.branch_targets;
    subsidiaryTotals.value = response.data.subsidiary_totals;
  } catch (error) {
    ElMessage.error('Failed to fetch targets');
  } finally {
    loading.value = false;
  }
};

const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary');
    subsidiaries.value = response.data;
  } catch (error) {
    ElMessage.error('Failed to fetch subsidiaries');
  }
};

const fetchBranches = async (subsidiaryId) => {
  if (!subsidiaryId) {
    ElMessage.error('Subsidiary ID is required');
    return;
  }
  try {
    const response = await api.get(`/api/finance/subsidiary/${subsidiaryId}/branches`);
    branches.value = response.data;
  } catch (error) {
    ElMessage.error('Failed to fetch branches');
  }
};

const showAddDialog = () => {
  isEditing.value = false;
  isEditingSubsidiary.value = false;
  editingBranch.value = null;
  editingSubsidiary.value = null;
  form.value = {
    subsidiary_id: '',
    year: new Date().getFullYear()
  };
  dialogVisible.value = true;
};

const editTarget = async (target) => {
  isEditing.value = true;
  isEditingSubsidiary.value = true;
  editingSubsidiary.value = {
    id: target.id,
    name: target.name,
    yearly_target: target.yearly_target
  };
  
  form.value = {
    subsidiary_id: target.id,
    year: selectedYear.value
  };
  
  // Fetch all branches for this subsidiary
  try {
    const response = await api.get(`/api/branches/subsidiary/${target.id}`);
    branches.value = response.data;
  } catch (error) {
    ElMessage.error('Failed to fetch branches');
  }
  
  dialogVisible.value = true;
};

const deleteTarget = async (target) => {
  try {
    
    await ElMessageBox.confirm(
      'Are you sure you want to delete this target?',
      'Warning',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    );

    // First, delete all branch monthly targets for this subsidiary
    const branchTargets = getBranchTargets(target.id);
    
    let branchDeletionSuccess = true;
    let deletedBranchCount = 0;
    
    for (const branchTarget of branchTargets) {
      try {
        await api.delete(`/api/finance/branch-monthly-targets/${branchTarget.branch_id}/${selectedYear.value}`);
        deletedBranchCount++;
      } catch (error) {
        branchDeletionSuccess = false;
      }
    }

    // Then delete the yearly target
    const url = `/api/finance/yearly-targets/${target.id}/${selectedYear.value}`;
    
    try {
      await api.delete(url);
      ElMessage.success('Target deleted successfully');
    } catch (error) {
      // If yearly target doesn't exist (404), that's okay - we've already deleted the branch targets
      if (error.response?.status === 404) {
        if (deletedBranchCount > 0) {
          ElMessage.success(`Successfully deleted ${deletedBranchCount} branch targets`);
        } else {
          ElMessage.warning('No targets were found to delete');
        }
        // Don't treat 404 as an error since it's an expected case
        fetchTargets();
        return;
      }
      throw error; // Re-throw other errors
    }
    
    fetchTargets();
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('Failed to delete target');
    }
  }
};

const submitForm = async () => {
  if (!formRef.value) return;

  try {
    await formRef.value.validate();
    
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user || !user.id) {
      ElMessage.error('User information not found');
      return;
    }

    if (isEditing.value) {
      if (isEditingSubsidiary.value) {
        // Handle subsidiary target update
        const branches = getSubsidiaryBranches.value;
        if (branches.some(branch => branch.target_amount === undefined || branch.target_amount === null)) {
          ElMessage.error('All branches must have a target amount');
          return;
        }

        // Update all branches under this subsidiary
        const promises = branches.map(branch => 
          api.post('/api/finance/branch-monthly-targets', {
            subsidiary_id: form.value.subsidiary_id,
            branch_id: branch.id,
            year: form.value.year,
            target_amount: parseFloat(branch.target_amount) || 0,
            created_by: user.id
          })
        );

        await Promise.all(promises);
        ElMessage.success('Subsidiary targets updated successfully');
      } else {
        // Handle single branch edit
        if (!form.value.target_amount) {
          ElMessage.error('Monthly target is required');
          return;
        }

        await api.post('/api/finance/branch-monthly-targets', {
          subsidiary_id: form.value.subsidiary_id,
          branch_id: editingBranch.value.branch_id,
          year: form.value.year,
          target_amount: parseFloat(form.value.target_amount) || 0,
          created_by: user.id
        });

        ElMessage.success('Branch target updated successfully');
      }
    } else {
      // Handle multiple branch creation
      if (!form.value.subsidiary_id || !form.value.year) {
        ElMessage.error('Subsidiary and Year are required');
        return;
      }

      // Submit targets for each branch
      const promises = filteredBranches.value.map(async (branch) => {
        if (branch.target_amount === undefined || branch.target_amount === null) {
          return; // Skip branches without target amount
        }

        await api.post('/api/finance/branch-monthly-targets', {
          subsidiary_id: form.value.subsidiary_id,
          branch_id: branch.id,
          year: form.value.year,
          target_amount: parseFloat(branch.target_amount) || 0,
          created_by: user.id
        });
      });

      await Promise.all(promises);
      ElMessage.success('Targets saved successfully for all months');
    }

    dialogVisible.value = false;
    fetchTargets();
  } catch (error) {
    if (error.response) {
      ElMessage.error(error.response.data.error || 'Failed to save targets');
    } else {
      ElMessage.error('Failed to save targets');
    }
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR'
  }).format(value);
};

const exportToCSV = async () => {
  try {
    const response = await api.get('/api/finance/branch-monthly-targets', {
      params: { year: selectedYear.value }
    });
    
    // Access the correct data structure from the response
    const branchTargets = response.data.branch_targets || [];
    const subsidiaryTotals = response.data.subsidiary_totals || [];

    const data = [
      ['Subsidiary', 'Branch', 'Month', 'Target Amount', 'Created By']
    ];

    // Add branch targets
    branchTargets.forEach(target => {
      // Format month as name if it's a number
      let monthLabel = target.month;
      if (typeof monthLabel === 'number') {
        monthLabel = new Date(0, monthLabel - 1).toLocaleString('default', { month: 'long' });
      }
      data.push([
        `"${target.subsidiary_name || ''}"`,
        `"${target.branch_name || ''}"`,
        `"${monthLabel || ''}"`,
        `"${target.monthly_target !== undefined ? target.monthly_target : ''}"`,
        `"${target.created_by_name || ''}"`
      ]);
    });

    // Add subsidiary totals
    subsidiaryTotals.forEach(total => {
      const subsidiary = subsidiaries.value.find(s => s.id === total.subsidiary_id);
      if (subsidiary) {
        data.push([
          `"${subsidiary.name}"`,
          '"Total"',
          '"Yearly"',
          `"${total.yearly_total}"`,
          '""'
        ]);
      }
    });

    const csvContent = data.map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `branch_monthly_targets_${selectedYear.value}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    ElMessage.success('Targets exported successfully');
  } catch (error) {
    ElMessage.error('Failed to export targets');
  }
};

// Add computed property for filtered branches
const filteredBranches = computed(() => {
  if (!form.value.subsidiary_id) return [];
  return branches.value.filter(branch => branch.subsidiary_id === form.value.subsidiary_id);
});

// Add new state for expanded rows
const expandedRows = ref([]);

// Update subsidiaryTargets computed property
const subsidiaryTargets = computed(() => {
  const targetsMap = {};
  
  // Initialize with yearly totals
  subsidiaryTotals.value.forEach(total => {
    const subsidiary = subsidiaries.value.find(s => s.id === total.subsidiary_id);
    if (subsidiary) {
      targetsMap[total.subsidiary_id] = {
        id: total.subsidiary_id,
        name: subsidiary.name,
        yearly_target: parseFloat(total.yearly_total) || 0,
        created_by_name: targets.value.find(t => t.subsidiary_id === total.subsidiary_id)?.created_by_name
      };
    }
  });
  
  return Object.values(targetsMap);
});

// Update getBranchTargets method
const getBranchTargets = (subsidiaryId) => {
  return targets.value
    .filter(target => target.subsidiary_id === subsidiaryId)
    .map(target => ({
      ...target,
      target_amount: target.monthly_target
    }));
};

// Update editBranchTarget method
const editBranchTarget = (target) => {
  isEditing.value = true;
  isEditingSubsidiary.value = false;
  editingBranch.value = {
    branch_id: target.branch_id,
    branch_name: target.branch_name,
    subsidiary_id: target.subsidiary_id,
    year: target.year
  };
  
  form.value = {
    subsidiary_id: target.subsidiary_id,
    year: target.year,
    target_amount: parseFloat(target.monthly_target) || 0
  };
  
  dialogVisible.value = true;
};

// Update deleteBranchTarget method
const deleteBranchTarget = async (target) => {
  try {
    await ElMessageBox.confirm(
      'Are you sure you want to delete this target?',
      'Warning',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    );
    
    await api.delete(`/api/finance/branch-monthly-targets/${target.branch_id}/${selectedYear.value}`);
    ElMessage.success('Target deleted successfully');
    fetchTargets();
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('Failed to delete target');
    }
  }
};

// Add method to toggle row expansion
const toggleExpand = (subsidiaryId) => {
  const index = expandedRows.value.indexOf(subsidiaryId);
  if (index === -1) {
    expandedRows.value.push(subsidiaryId);
  } else {
    expandedRows.value.splice(index, 1);
  }
};

// Add computed property for dialog title
const getDialogTitle = computed(() => {
  if (isEditing.value) {
    return isEditingSubsidiary.value ? 'Edit Subsidiary Target' : 'Edit Branch Target';
  }
  return 'Add Target';
});

// Add computed property for subsidiary branches
const getSubsidiaryBranches = computed(() => {
  if (!editingSubsidiary.value) return [];
  
  // Get all branches for this subsidiary
  const allBranches = branches.value.filter(branch => branch.subsidiary_id === editingSubsidiary.value.id);
  
  // Map branches and their targets
  return allBranches.map(branch => {
    // Find existing target for this branch
    const existingTarget = targets.value.find(target => 
      target.subsidiary_id === editingSubsidiary.value.id && 
      target.branch_id === branch.id
    );
    
    return {
      id: branch.id,
      branch_name: branch.branch_name,
      target_amount: existingTarget ? parseFloat(existingTarget.monthly_target) || 0 : 0
    };
  });
});

const fetchBulkData = async () => {
  try {
    const [subsRes, yearlyTargetsRes, targetsRes] = await Promise.all([
      api.get('/api/subsidiary'),
      api.get('/api/finance/yearly-targets', { params: { year: selectedYear.value } }),
      api.get('/api/finance/branch-monthly-targets', { params: { year: selectedYear.value } })
    ]);
    const targetMap = {};
    (yearlyTargetsRes.data || []).forEach(yt => {
      targetMap[yt.subsidiary_id] = parseFloat(yt.target_amount) || 0;
    });
    (targetsRes.data.subsidiary_totals || []).forEach(t => {
      if (targetMap[t.subsidiary_id] === undefined) {
        targetMap[t.subsidiary_id] = parseFloat(t.yearly_total) || 0;
      }
    });
    bulkRows.value = subsRes.data.map(s => ({
      subsidiary_id: s.id,
      subsidiary_name: s.name,
      yearly_target: targetMap[s.id] || 0
    }));
  } catch (err) {
    ElMessage.error('Failed to load subsidiaries');
  }
};

const saveBulkTargets = async () => {
  savingBulk.value = true;
  try {
    const items = bulkRows.value.map(r => ({
      subsidiary_id: r.subsidiary_id,
      yearly_target: parseFloat(r.yearly_target) || 0
    }));
    await api.post('/api/finance/branch-monthly-targets/bulk', {
      year: selectedYear.value,
      items
    });
    ElMessage.success('Targets saved');
    fetchBulkData();
    fetchTargets();
  } catch (err) {
    ElMessage.error('Failed to save targets');
  } finally {
    savingBulk.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchTargets();
  fetchSubsidiaries();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.yearly-targets {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 8px;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
}

.records-container {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e0f2fe;
  color: #0ea5e9;
}

.header-icon i {
  font-size: 1.5rem;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #1e40af;
}

.tab-btn.active {
  color: #1e40af;
  border-bottom-color: #1e40af;
}

.bulk-table {
  overflow-x: auto;
}

.bulk-table table {
  width: 100%;
  border-collapse: collapse;
}

.bulk-table th,
.bulk-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.bulk-table th {
  background: #f8fafc;
  font-weight: 500;
  color: #64748b;
  font-size: 0.875rem;
}

.target-input {
  width: 140px;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
}

.target-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.filters {
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
}

select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
  padding-right: 40px;
}

select:hover {
  border-color: #94a3b8;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.export-btn:hover {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.targets-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f8fafc;
  font-weight: 500;
  color: #64748b;
  font-size: 0.875rem;
}

tr:hover {
  background: #f8fafc;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.edit-btn, .delete-btn {
  background: none;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-btn {
  color: #0ea5e9;
}

.edit-btn:hover {
  background: #e0f2fe;
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  background: #fee2e2;
}

.target-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.target-dialog :deep(.el-form-item__label) {
  font-weight: 500;
  color: #1e293b;
}

.target-dialog :deep(.el-input__wrapper),
.target-dialog :deep(.el-input-number__wrapper) {
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.target-dialog :deep(.el-input__wrapper:hover),
.target-dialog :deep(.el-input-number__wrapper:hover) {
  border-color: #94a3b8;
}

.target-dialog :deep(.el-input__wrapper:focus-within),
.target-dialog :deep(.el-input-number__wrapper:focus-within) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.full-width {
  width: 100%;
}

.no-data {
  text-align: center;
  padding: 40px 0;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;
}

.no-data-message i {
  font-size: 24px;
  color: #94a3b8;
}

.no-data-message p {
  font-size: 14px;
  margin: 0;
}

@media (max-width: 768px) {
  .yearly-targets {
    padding: 8px;
  }
  .header {
    margin-bottom: 16px;
  }
  .header h1 {
    font-size: 1.25rem;
  }
  .header p {
    font-size: 0.75rem;
  }
  .records-container {
    padding: 12px;
    border-radius: 8px;
  }
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
  .header-icon {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
    border-radius: 8px;
  }
  .card-header h2 {
    font-size: 1rem;
  }
  .filters {
    margin-bottom: 12px;
  }
  .action-bar {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  .export-btn, .add-btn {
    width: 100%;
    justify-content: center;
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 5px;
  }
  .targets-table {
    overflow-x: auto;
  }
  th, td {
    padding: 8px;
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .yearly-targets {
    padding: 4px;
  }
  .records-container {
    padding: 6px;
    border-radius: 6px;
  }
  .header h1 {
    font-size: 1rem;
  }
  .header p {
    font-size: 0.7rem;
  }
  .card-header {
    padding-bottom: 6px;
  }
  th, td {
    padding: 4px;
    font-size: 0.7rem;
  }
}

.subsidiary-row {
  background-color: #f8fafc;
  font-weight: 500;
}

.branch-rows {
  background-color: #ffffff;
}

.branch-table {
  width: 100%;
  margin: 0;
  border-top: 1px solid #e2e8f0;
}

.branch-table th,
.branch-table td {
  padding: 8px 12px;
  font-size: 0.9em;
}

.branch-rows {
  transition: all 0.3s ease;
}

.expand-button {
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

.expand-button:hover {
  color: #0ea5e9;
  background: #e0f2fe;
  border-radius: 4px;
}
</style> 